import { useCallback, useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash, Pause, Play } from "@phosphor-icons/react";
import { toast } from "sonner";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg).join(", ") : d || "Aktion fehlgeschlagen";
};
const fmt = (s) => (s ? s.split("-").reverse().join(".") : "");
const valueText = (c) => (c.kind === "percent" ? `${String(c.value).replace(".", ",")} %` : euro(c.value));

const CodeRow = ({ c, onChange }) => {
  const toggle = async () => {
    try { await api.patch(`/discount-codes/${c.id}`, { status: c.status === "active" ? "paused" : "active" }); onChange(); }
    catch (e) { toast.error(errText(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Rabattcode ${c.code} wirklich löschen?`)) return;
    try { await api.delete(`/discount-codes/${c.id}`); toast.success("Code gelöscht"); onChange(); }
    catch (e) { toast.error(errText(e)); }
  };
  const active = c.status === "active";
  return (
    <tr className="border-t border-border" data-testid={`code-row-${c.code}`}>
      <td className="py-2 font-mono font-semibold">{c.code}</td>
      <td>{valueText(c)}</td>
      <td className="text-xs text-muted-foreground">{c.valid_from || c.valid_to ? `${fmt(c.valid_from) || "…"} – ${fmt(c.valid_to) || "…"}` : "immer"}</td>
      <td className="text-center">{c.uses || 0}×</td>
      <td>
        <span className={`px-2 py-0.5 rounded-full text-xs ${active ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`code-status-${c.code}`}>
          {active ? "Aktiv" : "Auf Halt"}
        </span>
      </td>
      <td className="text-right whitespace-nowrap">
        <Button variant="ghost" size="sm" onClick={toggle} data-testid={`code-toggle-${c.code}`}>
          {active ? <><Pause size={14}/> Halt</> : <><Play size={14}/> Aktivieren</>}
        </Button>
        <Button variant="ghost" size="sm" onClick={del} className="text-red-600" data-testid={`code-delete-${c.code}`}><Trash size={14}/></Button>
      </td>
    </tr>
  );
};

export const DiscountCodesCard = () => {
  const [codes, setCodes] = useState([]);
  const [code, setCode] = useState("");
  const [kind, setKind] = useState("percent");
  const [value, setValue] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(() => { api.get("/discount-codes").then((r) => setCodes(r.data)).catch(() => toast.error("Rabattcodes konnten nicht geladen werden")); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!code.trim() || !(Number(value) > 0)) { toast.error("Bitte Code und Rabatt größer 0 eingeben"); return; }
    if (from && to && to < from) { toast.error("Gültig bis darf nicht vor Gültig von liegen"); return; }
    try {
      await api.post("/discount-codes", { code: code.trim(), kind, value: Number(value), valid_from: from || null, valid_to: to || null });
      toast.success(`Rabattcode ${code.trim().toUpperCase()} angelegt`);
      setCode(""); setValue(""); setFrom(""); setTo(""); load();
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="discount-codes-card">
      <div>
        <h3 className="font-semibold">Rabattcodes</h3>
        <p className="text-[11px] text-muted-foreground">Gelten für alle Unterkünfte, nur auf die Übernachtung (nicht Verpflegung), zusätzlich zum Einzelbelegungsrabatt und nur, wenn der Zeitraum frei ist. „Gültig von–bis“ bezieht sich auf die Reisedaten.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <label className="text-xs md:col-span-2">Code<Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))} placeholder="z. B. SOMMER10" data-testid="code-new-code"/></label>
        <label className="text-xs">Art
          <select className="w-full h-9 border border-border rounded-md px-2 text-sm bg-background" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="code-new-kind">
            <option value="percent">Prozent %</option><option value="amount">Betrag €</option>
          </select>
        </label>
        <label className="text-xs">Rabatt<Input type="number" min="0" step="0.5" value={value} onChange={(e) => setValue(e.target.value)} data-testid="code-new-value"/></label>
        <label className="text-xs">Gültig von<Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="code-new-from"/></label>
        <label className="text-xs">Gültig bis<Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="code-new-to"/></label>
      </div>
      <Button onClick={create} className="rounded-xl" data-testid="code-create">Rabattcode erstellen</Button>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="code-table">
          <thead><tr className="text-xs text-muted-foreground text-left"><th className="py-1">Code</th><th>Rabatt</th><th>Gültig</th><th className="text-center">Genutzt</th><th>Status</th><th/></tr></thead>
          <tbody>
            {codes.map((c) => <CodeRow key={c.id} c={c} onChange={load}/>)}
            {!codes.length && <tr><td colSpan={6} className="py-3 text-xs text-muted-foreground">Noch keine Rabattcodes angelegt.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
